/**
 * Which state the cash receipt detail's Task card is in, from the receipt's
 * open logging task (the work item's summary), the full record of that task,
 * and the receipt's latest logging task of any status.
 */

import { isTerminalTaskStatus } from '@erp/shared-types';
import type { CashReceiptTaskSummary, TaskResponse } from '@erp/rpc';

export type CashReceiptTaskCardState =
  | { kind: 'loading' }
  | { kind: 'none' }
  | {
      kind: 'open';
      taskId: string;
      displayId: string | null;
      /** Null while loading, or when the viewer may not read tasks. */
      task: TaskResponse | null;
      needsInput: boolean;
    }
  | { kind: 'closed'; task: TaskResponse };

export function resolveCashReceiptTaskCardState({
  isLoading,
  openTask,
  openTaskDetail,
  latestTask,
}: {
  isLoading: boolean;
  /** The work item's open-task summary; null when no task is open. */
  openTask: CashReceiptTaskSummary | null;
  /** `tasks.get` for the open task, once loaded. */
  openTaskDetail: TaskResponse | null | undefined;
  /**
   * The receipt's current logging task: its most recently created one, any
   * status, skipping fold-cancelled duplicates (`useCashReceiptLatestLogTask`).
   */
  latestTask: TaskResponse | null | undefined;
}): CashReceiptTaskCardState {
  if (isLoading) return { kind: 'loading' };

  const detail =
    openTaskDetail && openTask && openTaskDetail.id === openTask.id
      ? openTaskDetail
      : null;
  // The detail polls; the work item can lag a close made elsewhere.
  if (detail && isTerminalTaskStatus(detail.status))
    return { kind: 'closed', task: detail };
  if (openTask) {
    const status = detail?.status ?? openTask.status;
    return {
      kind: 'open',
      taskId: openTask.id,
      displayId: detail?.displayId ?? openTask.displayId,
      task: detail,
      needsInput: status === 'needs_input',
    };
  }

  if (!latestTask) return { kind: 'none' };
  if (isTerminalTaskStatus(latestTask.status))
    return { kind: 'closed', task: latestTask };
  // Open per the task list but not yet in the (stale) work item.
  return {
    kind: 'open',
    taskId: latestTask.id,
    displayId: latestTask.displayId,
    task: latestTask,
    needsInput: latestTask.status === 'needs_input',
  };
}

function metadataString(
  metadata: TaskResponse['metadata'],
  key: string,
): string | undefined {
  const value = metadata?.[key];
  return typeof value === 'string' && value.trim() ? value : undefined;
}

/** The task's summary and agent summary, blank ones dropped. */
export function readTaskSummaries(metadata: TaskResponse['metadata']): {
  summary: string | undefined;
  agentSummary: string | undefined;
} {
  return {
    summary: metadataString(metadata, 'summary'),
    agentSummary: metadataString(metadata, 'agentSummary'),
  };
}
